import { computed, inject, Injectable, PLATFORM_ID, signal, Signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SpinnerService } from '../services/spinner.service';
import { NotificationService } from '../notification/notification.service';
import { CategoryService } from '../services/category.service';
import { catchError, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CategoryStore {
  private categoryService = inject(CategoryService);
  private spinnerService = inject(SpinnerService);
  private notificationService = inject(NotificationService);
  private platformId = inject(PLATFORM_ID);

  readonly activeCategoryId = computed(() => {
    const categories = this.state().data;
    if (!categories) return null;
    const active = categories.find(c => c.isActive);
    return active ? active.id : null;
  });

  private state = signal<{
    data: any[] | null;
    loading: boolean;
    error: any;
  }>({
    data: null,
    loading: false,
    error: null
  });

  loadCategories() {
    if (this.state().loading || this.state().data) return;

    const isBrowser = isPlatformBrowser(this.platformId);

    if (isBrowser) {
      this.spinnerService.show();
    }

    this.state.update(s => ({ ...s, loading: true, error: null }));

    this.categoryService.getAllCategory().pipe(
      catchError(err => {
        this.notificationService.errorHandler(err);
        this.state.update(s => ({ ...s, error: err, loading: false }));

        if (isBrowser) {
          this.spinnerService.hide();
        }
        return of(null);
      })
    ).subscribe(result => {
      if (isBrowser) {
        this.spinnerService.hide();
      }

      if (!result) return;
      let categories = result.body?.data || [];

      categories = categories.sort((a: any, b: any) => {
        const orderA = a.order != null ? Number(a.order) : Number.MAX_SAFE_INTEGER;
        const orderB = b.order != null ? Number(b.order) : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });

      this.state.update(s => ({
        ...s,
        data: categories,
        loading: false
      }));
    });
  }

  public selectCategories(): Signal<any[] | null> {
    return computed(() => this.state().data);
  }

  setActiveCategory(categoryId: string) {
    this.state.update(s => {
      if (!s.data) return s;

      const updatedData = s.data.map(cat => ({
        ...cat,
        isActive: cat.id === categoryId
      }));

      return { ...s, data: updatedData };
    });
  }

  refresh() {
    this.state.set({ data: null, loading: false, error: null });
    this.loadCategories();
  }
}